/**
 * Hardware del servidor (variante tarjetas).
 *
 * Solo CPU, RAM, GPU, fuentes y «otros». Las NIC viven en Red / IP, las HBA y
 * controladoras RAID en Almacenamiento, y las PSU también se reflejan en
 * Alimentación. Así «Hardware» no es un cajón que mezcle todo el inventario.
 *
 * La edición fina sigue en `ServerComponentsPanel` (botón «Inventario»).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../services/api';

const TIPOS = [
  { key: 'cpu', label: 'Procesadores' },
  { key: 'ram', label: 'Memoria RAM' },
  { key: 'gpu', label: 'GPU' },
  { key: 'psu', label: 'Fuentes de alimentación' },
  { key: 'other', label: 'Otros componentes' },
];

function parseNotes(raw) {
  try {
    const data = JSON.parse(raw || '{}');
    return data && typeof data === 'object' ? data : {};
  } catch {
    return {};
  }
}

function detalleTecnico(comp) {
  const d = parseNotes(comp.notes);
  if (comp.component_type === 'cpu') {
    return [
      d.cores ? `${d.cores}C/${d.threads || d.cores}T` : null,
      d.speed_mhz ? `${d.speed_mhz} MHz` : null,
      d.hyperthreading ? 'HT' : null,
    ].filter(Boolean).join(' · ');
  }
  if (comp.component_type === 'ram') {
    return [
      d.capacity_gb ? `${d.capacity_gb} GB` : null,
      d.type,
      d.ecc ? 'ECC' : null,
      d.speed_mhz ? `${d.speed_mhz} MHz` : null,
    ].filter(Boolean).join(' · ');
  }
  if (comp.component_type === 'psu') {
    return [
      comp.power_watts ? `${comp.power_watts} W` : null,
      comp.connector_type,
      comp.efficiency,
    ].filter(Boolean).join(' · ');
  }
  if (comp.notes && !comp.notes.trim().startsWith('{')) return comp.notes;
  return '';
}

function nombre(comp) {
  return [comp.manufacturer, comp.model].filter(Boolean).join(' ') || `Componente #${comp.id}`;
}

export default function ServerCardsHardware({ serverId, companyId, onOpenInventory, onCountChange }) {
  const [componentes, setComponentes] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');

  const cargar = useCallback(async () => {
    if (!serverId || !companyId) return;
    setCargando(true);
    setError('');
    try {
      const res = await api.getComponents(serverId, companyId);
      const lista = Array.isArray(res?.data) ? res.data : [];
      setComponentes(lista);
      onCountChange?.(lista.filter((c) => c.component_type === 'nic').length);
    } catch (e) {
      setError(e.message || 'No se pudieron cargar los componentes');
    } finally {
      setCargando(false);
    }
  }, [serverId, companyId, onCountChange]);

  useEffect(() => { cargar(); }, [cargar]);

  const porTipo = useMemo(() => {
    const map = {};
    TIPOS.forEach(({ key }) => {
      map[key] = componentes.filter((c) => c.component_type === key);
    });
    return map;
  }, [componentes]);

  const totalRamGb = useMemo(() => (
    porTipo.ram.reduce((acc, c) => acc + (Number(parseNotes(c.notes).capacity_gb) || 0), 0)
  ), [porTipo.ram]);

  const nucleos = useMemo(() => (
    porTipo.cpu.reduce((acc, c) => acc + (Number(parseNotes(c.notes).cores) || 0), 0)
  ), [porTipo.cpu]);

  if (cargando) {
    return <div className="sf2-card"><div className="sf2-empty">Cargando hardware…</div></div>;
  }

  if (error) {
    return <div className="sf2-card"><div className="sf2-empty">{error}</div></div>;
  }

  const sinNada = TIPOS.every(({ key }) => porTipo[key].length === 0);

  return (
    <div className="sf2-summary">
      <div className="sf2-card">
        <h3>
          Resumen de hardware
          {onOpenInventory && (
            <button type="button" className="sf2-link" onClick={onOpenInventory}>Inventario completo</button>
          )}
        </h3>
        <div className="sf2-hl">
          <div className="sf2-kpi"><span>Componentes</span><b>{componentes.length}</b></div>
          <div className="sf2-kpi"><span>Núcleos</span><b>{nucleos || '—'}</b></div>
          <div className="sf2-kpi"><span>RAM</span><b>{totalRamGb ? `${totalRamGb} GB` : '—'}</b></div>
          <div className="sf2-kpi"><span>Fuentes</span><b>{porTipo.psu.length}</b></div>
        </div>
      </div>

      {sinNada && (
        <div className="sf2-card">
          <div className="sf2-empty">
            El agente no ha reportado hardware todavía.
            {onOpenInventory && (
              <div style={{ marginTop: 10 }}>
                <button type="button" className="dv-btn dv-btn-secondary" onClick={onOpenInventory}>
                  Abrir inventario de componentes
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {TIPOS.map(({ key, label }) => {
        const items = porTipo[key];
        if (!items.length) return null;
        return (
          <div className="sf2-card" key={key}>
            <h3>{label}<span className="sf2-count">{items.length}</span></h3>
            <div className="sf2-rows">
              {items.map((comp) => {
                const detalle = detalleTecnico(comp);
                const extra = [
                  comp.slot ? `Slot ${comp.slot}` : null,
                  comp.serial_number ? `S/N ${comp.serial_number}` : null,
                ].filter(Boolean).join(' · ');
                return (
                  <div className="sf2-row" key={comp.id}>
                    <div className="sf2-row-main">
                      <b>{nombre(comp)}</b>
                      {(detalle || extra) && <small>{[detalle, extra].filter(Boolean).join(' · ')}</small>}
                    </div>
                    <span className="sf2-pill">{comp.status || 'activo'}</span>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
